import { Link } from 'gatsby';
import { REGION, RegionEnum } from 'gatsby-theme-hem-store/src/constants';
import capitalize from 'lodash/capitalize';
import groupBy from 'lodash/groupBy';
import isString from 'lodash/isString';
import pick from 'lodash/pick';
import sortBy from 'lodash/sortBy';
import startCase from 'lodash/startCase';
import React from 'react';

import { getMaterialsTechDetails } from '../utils/getMaterialTechDetails';
import { valueAsMetricOrImperial } from '../utils/valueAsMetricOrImperial';

const GENERAL_PICK = [
  'stackabilityMaxQty',
  'stackabilityMaxHeight',
  'countryOfOrigin',
  'forestryCertification',
  'insideMaterial',
  'lightbulb',
  'dimmable',
  'additionalNotesExternal',
];

const DIMENSIONS_PICK = [
  'length',
  'width',
  'depth',
  'heightCm',
  'heightIn',
  'diameter',
  'capacity',
  'seatWidth',
  'seatHeightCm',
  'seatHeightIn',
  'seatDepth',
  'backrestHeightCm',
  'backrestHeightIn',
  'armrestHeight',
  'cableLength',
  'pileHeight',
  'weight',
  'shelfDepth',
  'shelfWidth',
];

const PACKAGING_PICK = [
  'boxes',
  'packageVolume',
  'grossWeight',
  'packageWidth',
  'packageLength',
  'packageHeight',
];

export const TechnicalDetails: React.FC<{
  variant: Queries.VariantPageQuery['variant'];
}> = ({ variant }) => {
  const dimensions = pick(variant.techinalDetails, DIMENSIONS_PICK);
  const hasDimensions = Object.entries(dimensions).some(([, value]) => !!value);
  const packaging = pick(variant.techinalDetails, PACKAGING_PICK);
  const hasPackaging = Object.entries(packaging).some(([, value]) => !!value);

  const primaryMaterialTechDetails = getMaterialsTechDetails(
    // @ts-expect-error typing miss match
    variant.airtable?.primaryMaterial,
    variant.airtable?.primaryMaterial?.category === 'Combo',
  );

  const secondaryMaterialTechDetails = getMaterialsTechDetails(
    // @ts-expect-error typing miss match
    variant.airtable?.secondaryMaterial,
    variant.airtable?.secondaryMaterial?.category === 'Combo',
  );

  return (
    <>
      <div className="space-y-4">
        <Details
          title="General"
          data={{
            'Article number': variant.sku,
            warranty: variant.airtable.product.warranty,
            ...pick(variant.techinalDetails, GENERAL_PICK),
          }}
        />

        {hasDimensions && (
          <Details
            title="Dimensions"
            data={pick(variant.techinalDetails, DIMENSIONS_PICK)}
          />
        )}

        {hasPackaging && (
          <Details
            title="Packaging"
            data={pick(variant.techinalDetails, PACKAGING_PICK)}
          />
        )}

        {variant.techinalDetails.certifications &&
          variant.techinalDetails.certifications.length > 0 && (
            <>
              <div>
                <h4 className="mb-1 text-base font-bold">Certifications</h4>
                <dl className="grid grid-cols-2 gap-x-2 gap-y-1 text-base md:grid-cols-[1fr_2fr] md:gap-x-4">
                  {Object.entries(
                    groupBy(variant.techinalDetails.certifications, 'category'),
                  ).map(([category, certifications]) => {
                    return (
                      <React.Fragment key={category}>
                        <dd className="text-base text-black-600">{category}</dd>
                        <dt className="text-base tabular-nums">
                          <ul className="space-y-1">
                            {certifications.map((certification) => (
                              <li key={certification.slug}>
                                <Link
                                  to={certification.slug}
                                  className="link-primary"
                                >
                                  {certification.shortTitle}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </dt>
                      </React.Fragment>
                    );
                  })}
                </dl>
              </div>
            </>
          )}
      </div>

      <div className="mt-4 space-y-4 md:mt-0">
        {variant.airtable?.primaryMaterial &&
          React.Children.toArray(
            primaryMaterialTechDetails.map((techDetails) => (
              <Details
                title={`${
                  variant.airtable.primaryMaterialFor || 'Primary finish'
                } material`}
                samplesLink={techDetails.slug}
                data={techDetails}
              />
            )),
          )}

        {variant.airtable?.secondaryMaterial &&
          React.Children.toArray(
            secondaryMaterialTechDetails.map((techDetails) => (
              <Details
                title={`${
                  variant.airtable.secondaryMaterialFor || 'Secondary finish'
                } material`}
                samplesLink={techDetails.slug}
                data={techDetails}
              />
            )),
          )}
      </div>
    </>
  );
};

type DetailsProps = {
  type?: string;
  title?: string;
  data: { [key: string]: unknown };
  samplesLink?: string;
};

const keysWithCm = ['heightCm', 'seatHeightCm', 'backrestHeightCm'];

const keysWithIn = ['heightIn', 'seatHeightIn', 'backrestHeightIn'];

export const Details: React.FC<DetailsProps> = ({
  title,
  data,
  samplesLink,
}): JSX.Element => {
  return (
    <div>
      <h4 className="mb-1 text-base font-bold">
        {title}
        {isString(samplesLink) && (
          <Link
            to={samplesLink}
            className="link-primary ml-2"
            aria-label={`Buy ${title} sample`}
            data-selector="Buy sample link"
            data-selector-value={title}
          >
            Buy sample
          </Link>
        )}
      </h4>
      <dl className="grid grid-cols-2 gap-x-2 gap-y-1 text-base md:grid-cols-[1fr_2fr] md:gap-x-4">
        {sortBy(Object.entries(data), 'key')
          .filter(([, value]) => !!value)
          .filter(([key]) => key !== 'slug')
          .map(([key, value]) => {
            const energyEfficiencyLinks = {
              '20465':
                'https://eprel.ec.europa.eu/screen/product/lightsources/512229',
              '30656':
                'https://eprel.ec.europa.eu/screen/product/lightsources/512229',
              '20463':
                'https://eprel.ec.europa.eu/screen/product/lightsources/512227',
            };

            if (
              REGION === RegionEnum.Eu &&
              key === 'lightbulb' &&
              typeof data['Article number'] === 'string' &&
              data['Article number'] in energyEfficiencyLinks
            ) {
              const articleNumber = data['Article number'] as string;
              if (articleNumber in energyEfficiencyLinks) {
                value = `${value}. <a href="${energyEfficiencyLinks[articleNumber as keyof typeof energyEfficiencyLinks]}" target="_blank" rel="noopener noreferrer">Energy efficiency class</a>.`;
              }
            }

            if (key === 'stackabilityMaxQty') {
              key = 'stackability';
              value = `${data['stackabilityMaxQty']}|${data['stackabilityMaxHeight']}`;
            }

            if (key === 'stackabilityMaxHeight') {
              return null;
            }

            if (key === 'additionalNotesExternal') {
              key = 'Note';
            }

            if (
              REGION === RegionEnum.Us &&
              key === keysWithCm.find((k) => k === key)
            ) {
              return null;
            }

            if (
              REGION !== RegionEnum.Us &&
              key === keysWithIn.find((k) => k === key)
            ) {
              return null;
            }

            if (key === 'dimmable' && value === false) {
              return null;
            }

            // Remove In or Cm from the end of any key name
            key = key.replace(/(In|Cm)$/, '');

            return (
              <React.Fragment key={key}>
                <dd className="text-base text-black-600">
                  {capitalize(startCase(key)).replace('Ral', 'RAL')}
                </dd>
                {key === 'warranty' ? (
                  <dt className="text-base tabular-nums">
                    <Link to="/warranty" className="link-primary">
                      <>{value}</>
                    </Link>
                  </dt>
                ) : (
                  <dt
                    className="text-base tabular-nums"
                    dangerouslySetInnerHTML={{
                      __html: valueAsMetricOrImperial(key, value),
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
      </dl>
    </div>
  );
};
